import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelM, Text16 } from '@sorare/core/src/atoms/typography';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import Dialog from '@sorare/core/src/components/dialog';
import { Rank } from '@sorare/core/src/components/lobby/Rank';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { Nickname } from '@sorare/core/src/components/user/Nickname';
import SocialShare from '@sorare/core/src/components/user/SocialShare';
import { ANY_SPORT_PLAY_PRO_LINEUPS_ID } from '@sorare/core/src/constants/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { useAbsoluteURL } from '@sorare/core/src/hooks/useAbsoluteURL';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events';
import { glossary } from '@sorare/core/src/lib/glossary';
import { randomizedPollInterval } from '@sorare/core/src/lib/gql';
import { socialSharingMessages } from '@sorare/core/src/lib/share';

import DivisionLogo from 'components/so5/DivisionLogo';
import Points from 'components/so5/Points';
import So5LineupAppearance from 'components/so5/So5LineupAppearance';

import {
  So5RankingDetailsQuery,
  So5RankingDetailsQueryVariables,
} from './__generated__/index.graphql';

const POLL_INTERVAL = randomizedPollInterval(1);

const Header = styled.div`
  width: 100%;
  padding: 0 var(--unit);
  display: grid;
  align-items: center;
  grid-template-columns: 1fr min-content;
`;
const LoadingIndicatorWrapper = styled.div`
  padding: var(--unit) 0 var(--quadruple-unit);
`;
const Content = styled.div`
  overflow: auto;
`;
const Leaderboard = styled(SBHorizontal).attrs({ gap: 0 })`
  padding: 30px 20px;
  border-bottom: 1px solid var(--c-neutral-300);
`;
const Division = styled(Horizontal).attrs({ gap: 0 })`
  gap: 10px;
`;
const RankAndScore = styled(Vertical).attrs({ gap: 0 })`
  flex-shrink: 0;
  align-items: end;
`;
const TiebreakerScore = styled.div`
  text-align: right;
  padding: 10px;
`;

export const SO5_RANKING_DETAILS_QUERY = gql`
  query So5RankingDetailsQuery($id: ID!) {
    so5 {
      so5Ranking(id: $id) {
        id
        ranking
        score
        tiebreakerScore
        so5Leaderboard {
          slug
          displayName
          ...DivisionLogo_so5Leaderboard
        }
        so5Lineup {
          id
          cancelledAt
          user {
            slug
            ...Nickname_publicUserInfoInterface
          }
          so5Appearances {
            id
            ...So5LineupAppearance_so5Appearance
          }
          socialPictureUrls {
            ...SocialShare_SocialPictures
          }
        }
      }
    }
  }
  ${DivisionLogo.fragments.so5Leaderboard}
  ${So5LineupAppearance.fragments.so5Appearance}
  ${SocialShare.fragments.socialPictures}
  ${Nickname.fragments.user}
` as TypedDocumentNode<So5RankingDetailsQuery, So5RankingDetailsQueryVariables>;
type Props = {
  so5RankingId: string | null;
  onClose: () => void;
};

export const So5LineupDetails = (props: Props) => {
  const { formatMessage } = useIntlContext();
  const { generateSportContextPath } = useSportContext();
  const { so5RankingId, onClose } = props;
  const { loading, data } = useQuery(SO5_RANKING_DETAILS_QUERY, {
    variables: { id: idFromObject(so5RankingId)! },
    skip: !so5RankingId,
    pollInterval: POLL_INTERVAL,
  });

  const lineupUrl = useAbsoluteURL(
    generateSportContextPath(ANY_SPORT_PLAY_PRO_LINEUPS_ID, {
      params: {
        id: idFromObject(data?.so5.so5Ranking.so5Lineup.id) || '',
      },
    })
  );

  if (!data && !loading) {
    return null;
  }

  const { so5Ranking } = data?.so5 || {};
  const { ranking, score, so5Leaderboard, so5Lineup } = so5Ranking || {};
  const { cancelledAt, so5Appearances, user } = so5Lineup || {};

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open
      title={
        <Header>
          <Text16 bold className="text-center">
            {!!user && (
              <MyClubLink user={user}>
                <Nickname user={user} />
              </MyClubLink>
            )}
          </Text16>
          {so5Lineup && (
            <SocialShare
              url={lineupUrl}
              image={so5Lineup.socialPictureUrls}
              title={formatMessage(socialSharingMessages.lineup)}
              trackingEventName={socialShareEventName.SHARE_LINEUP}
              trackingEventContext={socialShareEventContext.LEADERBOARD}
              renderButton={({ ShareButton, label, Icon }) => (
                <ShareButton size="medium" startIcon={Icon}>
                  {label}
                </ShareButton>
              )}
            />
          )}
        </Header>
      }
      onClose={onClose}
    >
      {!data ? (
        <LoadingIndicatorWrapper>
          <LoadingIndicator />
        </LoadingIndicatorWrapper>
      ) : (
        <Content>
          <Leaderboard>
            <Division>
              {so5Leaderboard && (
                <>
                  <DivisionLogo so5Leaderboard={so5Leaderboard} />
                  <Text16 bold>
                    <Ellipsis>{so5Leaderboard.displayName}</Ellipsis>
                  </Text16>
                </>
              )}
            </Division>
            <RankAndScore>
              {cancelledAt ? (
                <Chip
                  label={<FormattedMessage {...glossary.canceled} />}
                  custom={{
                    color: 'white',
                    background: 'var(--c-red-600)',
                  }}
                  size="smaller"
                />
              ) : (
                <>
                  <Text16 bold as="div">
                    <Rank rank={ranking} />
                  </Text16>
                  <Text16 bold color="var(--c-neutral-600)">
                    <Points score={score} />
                  </Text16>
                </>
              )}
            </RankAndScore>
          </Leaderboard>
          <div>
            {so5Appearances?.map(so5Appearance => (
              <So5LineupAppearance
                key={so5Appearance.id}
                so5Appearance={so5Appearance}
              />
            ))}
          </div>
          {(so5Ranking?.tiebreakerScore || 0) > 0 && (
            <TiebreakerScore>
              <LabelM>
                <FormattedMessage
                  id="So5LienupDetails.tiebreakerScore"
                  defaultMessage="Tiebreaker score: <strong>{score, number, ::.00} pts</strong>"
                  values={{
                    strong: Bold,
                    score: so5Ranking?.tiebreakerScore,
                  }}
                />
              </LabelM>
            </TiebreakerScore>
          )}
        </Content>
      )}
    </Dialog>
  );
};

export default So5LineupDetails;
